import React, { useMemo } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import './RichTextEditor.css';
import { htmlToPlainText, plainTextToHtml } from '../utils/richText';

// Re-exported so callers that already import these from here keep working.
export { htmlToPlainText, plainTextToHtml };

/**
 * CKEditor 5, themed to the app.
 *
 * Replaces a textarea where formatting was typed as markdown (`## `, `• `,
 * `**bold**`) and only ever rendered as literal characters — the toolbar
 * inserted the symbols but nothing turned them back into formatting.
 *
 * The value is HTML. It is sanitised server-side on save rather than here,
 * because the description is served to the public embed board and cleaning it
 * on the way into the database is the only place that covers every reader.
 */

// Only items the classic build actually ships — naming one it does not have
// (horizontalLine, underline) drops it silently and logs a warning per render.
const TOOLBAR = [
  'heading', '|',
  'bold', 'italic', 'link', '|',
  'bulletedList', 'numberedList', '|',
  'outdent', 'indent', '|',
  'blockQuote', '|',
  'undo', 'redo',
];

export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Write or paste the job description here…',
  minHeight = 300,
  maxHeight = 460,
  disabled = false,
}) {
  const config = useMemo(() => ({
    toolbar: { items: TOOLBAR, shouldNotGroupWhenFull: true },
    placeholder,
    heading: {
      options: [
        { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
        { model: 'heading2', view: 'h2', title: 'Heading', class: 'ck-heading_heading2' },
        { model: 'heading3', view: 'h3', title: 'Subheading', class: 'ck-heading_heading3' },
      ],
    },
    link: { addTargetToExternalLinks: true, defaultProtocol: 'https://' },
  }), [placeholder]);

  const chars = htmlToPlainText(value).length;

  return (
    <div
      className="rte"
      style={{
        '--rte-min-height': `${minHeight}px`,
        '--rte-max-height': `${Math.max(maxHeight, minHeight)}px`,
      }}
    >
      <CKEditor
        editor={ClassicEditor}
        config={config}
        data={value || ''}
        disabled={disabled}
        onChange={(_, editor) => onChange(editor.getData())}
      />
      <div className="rte-footer">{chars} chars</div>
    </div>
  );
}
